import { forwardRef, useMemo, useState } from 'react';
import type { PropsWithoutRef, Ref, ReactElement, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import Button, { StyledButton } from '../Button';
import Icon from '../Icon';
import MenuButton from '../MenuButton';
import MetaList from '../MetaList';
import SummaryItem, {
  StyledPrimary,
  StyledSecondary,
  StyledSummaryItemActions,
  StyledVisual
} from '../SummaryItem';
import Progress, { StyledProgressRing } from '../Progress';
import Text from '../Text';
import type { ForwardRefForwardPropsComponent, BaseProps, NoChildrenProp } from '../../types';
import { defaultThemeProp } from '../../theme';
import { useI18n } from '../../hooks';

import FileVisual from './FileVisual';
import { getKindFromMimeType, getMimeTypeFromFile } from './utils';

export interface FileUploadItemProps extends BaseProps, NoChildrenProp {
  /** Name of the file. */
  name: string;
  /** Id applied to upload item */
  id?: string;
  /** File blob */
  File?: File;
  /** A FileUploadVisual element to represent the file. */
  visual?: ReactElement;
  /** Additional information about the file. If progress prop is passed and its value is less than 100, this region is instead used for the upload progress indicator. */
  meta?: string | string[];
  /** When passed, will render a single icon button for yet to be or in progress uploads. Use this action to trigger xhr request termination. */
  onCancel?: (name: FileUploadItemProps['name']) => void;
  /** When passed, will render a single icon button or within a MenuButton if other actions are defined. */
  onEdit?: (name: FileUploadItemProps['name']) => void;
  /** When passed, will render a single icon button or within a MenuButton if other actions are defined. */
  onPreview?: (name: FileUploadItemProps['name']) => void;
  /** When passed, will render a single icon button or within a MenuButton if other actions are defined. */
  onDownload?: (name: FileUploadItemProps['name']) => void;
  /** When passed, will render a single icon button or within a MenuButton if other actions are defined. */
  onDelete?: (name: FileUploadItemProps['name']) => void;
  /**
   * Set if there is an error uploading or processing the file. Applies error styling to the meta string passed.
   * @default false
   */
  error?: boolean;
  /** A string to be used as an image src for a attachment thumbnail. Falls back to a provided icon or the default attachment icon. */
  thumbnail?: string;
  /** If the attachment is in an upload state, pass progress as a number between 0-100 representing the percentage of progress. */
  progress?: number;
  /** The size of the file. */
  size?: number;
  /** Determines if upload item will be disabled */
  disabled?: boolean;
  /** A ref to the file item's HTMLLIElement. */
  ref?: Ref<HTMLLIElement>;
}

interface FileAction {
  fn: (name: FileUploadItemProps['name']) => void;
  text: string;
  icon: string;
}

const StyledFileUploadItem = styled(SummaryItem)<{ hasThumbnail?: boolean; disabled?: boolean }>(({
  hasThumbnail,
  disabled,
  theme
}) => {
  return css`
    border: 0.0625rem solid ${theme.base.palette['border-line']};
    border-radius: calc(0.5 * ${theme.base['border-radius']});
    background-color: ${theme.base.palette['primary-background']};
    padding: calc(0.5 * ${theme.base.spacing});
    min-height: 3rem;

    ${disabled &&
    css`
      opacity: ${theme.base['disabled-opacity']};

      ${StyledButton}:disabled {
        opacity: unset;
      }
    `}

    ${StyledProgressRing} {
      height: ${theme.components.input.height};
      width: ${theme.components.input.height};
    }

    ${hasThumbnail &&
    css`
      height: calc(6rem + ${theme.base.spacing});
      grid-row: span 2;
      grid-template-columns: auto minmax(0, 1fr) auto;

      ${StyledVisual} {
        align-self: auto;
        width: calc(6rem - 0.125rem);
        height: calc(6rem - 0.125rem);
      }

      ${StyledPrimary} {
        align-self: start;
        margin-block-start: calc(0.5 * ${theme.base.spacing});
      }

      ${StyledSecondary} {
        grid-area: primary;
        align-self: center;
        margin-block-start: calc(3 * ${theme.base.spacing});
      }

      ${StyledSummaryItemActions} {
        align-self: start;
      }
    `}
  `;
});

StyledFileUploadItem.defaultProps = defaultThemeProp;

/**
 *  @deprecated
 *  Deprecating in favour of new file components
 */
const FileUploadItem: ForwardRefForwardPropsComponent<FileUploadItemProps> = forwardRef(
  function FileUploadItem(
    {
      name,
      meta,
      error = false,
      progress,
      thumbnail,
      onCancel,
      onEdit,
      onPreview,
      onDownload,
      onDelete,
      size,
      disabled,
      ...restProps
    }: PropsWithoutRef<FileUploadItemProps>,
    ref: FileUploadItemProps['ref']
  ) {
    const t = useI18n();
    const [thumbnailError, setThumbnailError] = useState(false);

    const definedActions: FileAction[] = useMemo(
      () =>
        [
          { fn: onCancel, text: t('cancel'), icon: 'times' },
          { fn: onEdit, text: t('edit'), icon: 'pencil' },
          { fn: onPreview, text: t('preview'), icon: 'eye' },
          { fn: onDownload, text: t('download'), icon: 'download' },
          { fn: onDelete, text: t('delete'), icon: 'trash' }
        ].filter<FileAction>((action): action is FileAction => !!action.fn),
      [onCancel, onEdit, onPreview, onDownload, onDelete]
    );

    let metaContent: ReactNode = meta;
    let actions: ReactElement | undefined;

    const type = getKindFromMimeType(getMimeTypeFromFile(name) ?? '');

    if (typeof progress === 'number' && progress < 100) {
      metaContent = `${t('uploading')}…${Math.floor(progress)}%`;
    }

    if (Array.isArray(metaContent)) {
      metaContent = (
        <MetaList
          items={metaContent.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Text key={i} variant='secondary' status={error ? 'error' : undefined}>
              {item}
            </Text>
          ))}
        />
      );
    } else if (typeof metaContent === 'string') {
      metaContent = (
        <Text variant='secondary' status={error ? 'error' : undefined}>
          {metaContent}
        </Text>
      );
    }

    if (definedActions.length === 1) {
      const action = definedActions[0];

      actions = (
        <Button
          icon
          variant='simple'
          disabled={disabled}
          onClick={() => action.fn?.(name)}
          aria-label={`${action.text} ${name}`}
        >
          <Icon name={action.icon} />
        </Button>
      );
    } else if (definedActions.length > 1) {
      actions = (
        <MenuButton
          text={t('actions')}
          iconOnly
          icon='more'
          variant='simple'
          disabled={disabled}
          menu={{
            items: definedActions.map(action => ({
              id: action.text,
              primary: action.text,
              onClick: () => {
                action.fn(name);
              }
            }))
          }}
        />
      );
    }

    const visual = useMemo(() => {
      if (typeof progress === 'number' && progress !== 100) {
        return <Progress value={progress} placement='inline' variant='ring' />;
      }

      return (
        <FileVisual
          type={type}
          thumbnail={thumbnailError ? undefined : thumbnail}
          onThumbnailError={() => {
            setThumbnailError(true);
          }}
        />
      );
    }, [progress, thumbnail, thumbnailError]);

    return (
      <StyledFileUploadItem
        {...restProps}
        forwardedAs='li'
        hasThumbnail={
          !!thumbnail && !thumbnailError && (progress === 100 || progress === undefined) && !error
        }
        ref={ref}
        visual={visual}
        primary={name}
        secondary={metaContent}
        actions={actions}
        overflowStrategy='ellipsis'
        disabled={disabled}
      />
    );
  }
);

export default FileUploadItem;
